import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { getImageDimensions } from '../../../utils/mediaDetector';
import ImageCarousel from '../ImageCarousel';

/**
 * CleanContentRenderer - Renderiza conteúdo de forma ULTRA LIMPA
 * Suporta tanto formato JSON estruturado quanto HTML legado
 *
 * NOVO: Aceita contentBlocks (array JSON) ou htmlContent (string HTML)
 * Filosofia: Modal de leitura deve ser simples - texto + fotos, nada mais.
 */
const CleanContentRenderer = ({
  contentBlocks,
  htmlContent,
  allImages = [],
  className = '',
  sx = {},
  mediaType,
  contentType,
  carouselMedia = [], // Receber media estruturada
  embedHtml,
  videoStorageMethod
}) => {

  // Ref para o container do Instagram embed
  const instagramContainerRef = useRef(null);
  const instagramProcessedRef = useRef(false);

  // Extrair videoId do embedHtml para criar iframe direto
  const getTikTokVideoId = (html) => {
    if (!html) return null;
    // Procurar data-video-id no blockquote
    const videoIdMatch = html.match(/data-video-id="(\d+)"/);
    if (videoIdMatch) return videoIdMatch[1];
    // Procurar na URL cite
    const citeMatch = html.match(/cite="[^"]*\/video\/(\d+)/);
    if (citeMatch) return citeMatch[1];
    // Procurar qualquer sequência de números que pareça um ID
    const numMatch = html.match(/\/video\/(\d+)/);
    if (numMatch) return numMatch[1];
    return null;
  };

  const tiktokVideoId = mediaType === 'TikTok' ? getTikTokVideoId(embedHtml) : null;

  // Carregar script do Instagram quando necessário
  useEffect(() => {
    if (mediaType === 'Instagram' && embedHtml && instagramContainerRef.current) {
      // Reset quando o embedHtml mudar
      instagramProcessedRef.current = false;

      console.log('📸 Instagram: iniciando carregamento do embed...');

      const processInstagramEmbed = () => {
        if (instagramProcessedRef.current) {
          console.log('📸 Instagram: já processado, ignorando');
          return;
        }

        const container = instagramContainerRef.current;
        if (!container) {
          console.log('📸 Instagram: container não encontrado');
          return;
        }

        // Verificar se o blockquote está no DOM
        const blockquote = container.querySelector('.instagram-media');
        if (!blockquote) {
          console.log('📸 Instagram: blockquote não encontrado ainda, aguardando...');
          return;
        }

        console.log('📸 Instagram: blockquote encontrado no DOM');

        // Verificar se já tem um iframe (embed já carregou)
        const existingIframe = container.querySelector('iframe');
        if (existingIframe) {
          console.log('📸 Instagram: embed já carregado (iframe existe)');
          instagramProcessedRef.current = true;
          return;
        }

        // Verificar se script já existe
        const existingScript = document.querySelector('script[src="https://www.instagram.com/embed.js"]');

        const tryProcess = () => {
          if (window.instgrm && window.instgrm.Embeds) {
            console.log('📸 Instagram: chamando process()');
            try {
              window.instgrm.Embeds.process();
              instagramProcessedRef.current = true;
              console.log('📸 Instagram: process() executado com sucesso');
            } catch (error) {
              console.error('📸 Instagram: erro ao executar process():', error);
            }
          }
        };

        if (existingScript) {
          console.log('📸 Instagram: script já existe');
          if (window.instgrm && window.instgrm.Embeds) {
            console.log('📸 Instagram: biblioteca já disponível');
            tryProcess();
          } else {
            console.log('📸 Instagram: aguardando biblioteca carregar...');
            let attempts = 0;
            const checkLib = setInterval(() => {
              attempts++;
              console.log(`📸 Instagram: tentativa ${attempts}/30...`);
              if (window.instgrm && window.instgrm.Embeds) {
                console.log('📸 Instagram: biblioteca disponível');
                tryProcess();
                clearInterval(checkLib);
              }
              if (attempts >= 30) {
                console.log('📸 Instagram: timeout aguardando biblioteca');
                clearInterval(checkLib);
              }
            }, 300);
          }
        } else {
          // Criar novo script
          console.log('📸 Instagram: carregando script pela primeira vez');
          const script = document.createElement('script');
          script.src = 'https://www.instagram.com/embed.js';
          script.async = true;
          script.onload = () => {
            console.log('📸 Instagram: script carregou com sucesso');
            let attempts = 0;
            const checkLib = setInterval(() => {
              attempts++;
              console.log(`📸 Instagram: tentativa ${attempts}/30...`);
              if (window.instgrm && window.instgrm.Embeds) {
                console.log('📸 Instagram: biblioteca disponível após carregamento');
                tryProcess();
                clearInterval(checkLib);
              }
              if (attempts >= 30) {
                console.log('📸 Instagram: timeout após carregar script');
                clearInterval(checkLib);
              }
            }, 300);
          };
          script.onerror = (error) => {
            console.error('📸 Instagram: erro ao carregar script:', error);
          };
          document.body.appendChild(script);
        }
      };

      // Aguardar um pouco mais para garantir que o blockquote foi renderizado
      const timer = setTimeout(processInstagramEmbed, 500);
      return () => clearTimeout(timer);
    }
  }, [mediaType, embedHtml]);

  // Obter dimensões para imagens de redes sociais
  const getSocialMediaImageDimensions = () => {
    const socialMediaTypes = ['Twitter', 'Instagram', 'Facebook', 'YouTube', 'TikTok'];
    if (!socialMediaTypes.includes(mediaType)) {
      // Para artigos normais, usar 100%
      return {
        maxWidth: '100%',
        width: '100%',
        margin: '0 auto'
      };
    }

    // Usar dimensões do mediaDetector para redes sociais
    const dims = getImageDimensions({ mediaType, contentType });
    return {
      maxWidth: dims.maxWidth,
      width: dims.width,
      margin: dims.margin || '0 auto'
    };
  };

  const imageDimensions = getSocialMediaImageDimensions();

  /**
   * Detecta se estamos usando o novo formato JSON ou legado HTML
   */
  const isStructuredContent = contentBlocks && Array.isArray(contentBlocks);

  /**
   * Processa markdown em texto para detectar formatação
   * Retorna array de blocos processados
   */
  const processMarkdownText = (text) => {
    if (!text) return [];

    // Dividir por parágrafos (linhas duplas ou simples)
    const paragraphs = text.split(/\n+/).filter(p => p.trim().length > 0);
    const result = [];

    paragraphs.forEach(p => {
      const trimmed = p.trim();

      // Detectar títulos markdown (# Título, ## Subtítulo, etc.)
      const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        result.push({
          type: 'heading',
          level: headingMatch[1].length,
          text: headingMatch[2]
        });
        return;
      }

      // Detectar subtítulo em negrito (**texto**)
      const boldMatch = trimmed.match(/^\*\*(.+)\*\*$/);
      if (boldMatch) {
        result.push({
          type: 'heading',
          level: 3,
          text: boldMatch[1]
        });
        return;
      }

      // Detectar autor em itálico (*Por Nome*)
      const authorMatch = trimmed.match(/^\*(.+)\*$/);
      if (authorMatch) {
        result.push({
          type: 'paragraph',
          text: authorMatch[1],
          isAuthor: true
        });
        return;
      }

      // Ignorar linha de separação (---)
      if (/^-{3,}$/.test(trimmed)) {
        return;
      }

      // Parágrafo normal
      result.push({
        type: 'paragraph',
        text: trimmed
      });
    });

    return result;
  };

  /**
   * Converte blocos JSON estruturados para o formato interno do componente
   * Processa markdown em cada bloco de texto
   */
  const convertStructuredContent = (blocks) => {
    const textBlocks = [];
    const imageBlocks = [];

    blocks.forEach(block => {
      if (block.type === 'text') {
        // Processar markdown no conteúdo do bloco
        const processedBlocks = processMarkdownText(block.content);
        textBlocks.push(...processedBlocks);
      } else if (block.type === 'image') {
        imageBlocks.push({
          src: block.src,
          caption: block.caption
        });
      }
    });

    return { textBlocks, imageBlocks };
  };

  /**
   * Helper para extrair HTML seguro (preservando highlights)
   */
  const getSafeHtml = (node) => {
    let html = '';

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      const allowedTags = ['mark', 'strong', 'b', 'em', 'i', 'u', 'span', 'br'];

      // Se é uma tag permitida, abrimos a tag
      if (allowedTags.includes(tagName)) {
        let attributes = '';

        // Preservar estilo (importante para cor de fundo do highlight)
        if (node.hasAttribute('style')) {
          attributes += ` style="${node.getAttribute('style')}"`;
        }

        // Preservar classe (se necessário)
        if (node.hasAttribute('class')) {
          attributes += ` class="${node.getAttribute('class')}"`;
        }

        html += `<${tagName}${attributes}>`;
      }

      // Processar filhos recursivamente
      node.childNodes.forEach(child => {
        html += getSafeHtml(child);
      });

      // Se é uma tag permitida, fechamos a tag
      if (allowedTags.includes(tagName)) {
        html += `</${tagName}>`;
      }

      // Se não é permitida, apenas o conteúdo interno foi adicionado (unwrap)
    }

    return html;
  };

  /**
   * Extrai apenas texto limpo do HTML (modo legado)
   * Remove TUDO exceto o texto real dos parágrafos, MAS preserva highlights
   */
  const extractCleanText = (html) => {
    if (!html) return [];

    // Se não é HTML, usar processMarkdownText para suportar markdown completo
    if (!html.includes('<') || !html.includes('>')) {
      return processMarkdownText(html);
    }

    // Criar parser DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const paragraphs = [];
    const seenTexts = new Set();

    // Função para validar se é texto real (não lixo)
    const isValidText = (text) => {
      if (!text || text.length < 10) return false;

      // Filtrar APENAS textos que são DEFINITIVAMENTE lixo
      const junkPatterns = [
        /^(posted by|by |categories|tags|share this|follow us)$/i,
        /^\d+$/, // Apenas números soltos
        /^(advertisement|publicidade|anúncio)$/i,
        /^(twitter|facebook|instagram|whatsapp)$/i,
        /^(cookie policy|política de privacidade|termos de uso)$/i
      ];

      return !junkPatterns.some(pattern => pattern.test(text.trim()));
    };

    // Buscar container principal do artigo primeiro
    const mainSelectors = [
      'article',
      '.article-content',
      '.post-content',
      '.entry-content',
      'main',
      '[role="article"]'
    ];

    let contentRoot = null;
    for (const selector of mainSelectors) {
      contentRoot = doc.querySelector(selector);
      if (contentRoot) break;
    }

    // Fallback para body
    if (!contentRoot) {
      contentRoot = doc.body;
    }

    // ✅ LIMPEZA PRÉVIA: Remover elementos de navegação/UI ANTES de extrair parágrafos
    const elementsToRemove = contentRoot.querySelectorAll([
      'nav', 'header', 'footer', 'aside', 'menu',
      '.breadcrumb', '.breadcrumbs', '[class*="breadcrumb"]',
      '.navigation', '.nav', '[class*="navigation"]',
      '.share', '.social', '[class*="share"]', '[class*="social"]',
      '.comment', '[class*="comment"]',
      '.related', '[class*="related"]',
      '.sidebar', '.widget',
      'script', 'style', 'noscript'
    ].join(','));

    console.log(`🧹 Removendo ${elementsToRemove.length} elementos de navegação/UI`);
    elementsToRemove.forEach(el => el.remove());

    // Extrair APENAS parágrafos e headings do conteúdo limpo
    const textElements = contentRoot.querySelectorAll('p, h1, h2, h3, h4, h5, h6');

    console.log(`🔍 CleanContentRenderer - Elementos encontrados: ${textElements.length}`);

    let filtered = 0;
    let tooShort = 0;
    let duplicate = 0;
    let junk = 0;

    textElements.forEach(el => {
      // Usar textContent para validação e desduplicação
      const rawText = el.textContent.trim();

      // OBTER HTML SEGURO (com highlights)
      const safeHtml = getSafeHtml(el);

      const isHeading = /^h[1-6]$/i.test(el.tagName);

      // ✅ VERIFICAR: Ignorar elementos dentro de navegação/listas não-artigo
      const isInsideNavigation = el.closest('nav, header, footer, ol, ul[class*="breadcrumb"], aside');
      if (isInsideNavigation) {
        filtered++;
        return; // Pular este elemento
      }

      // Headings podem ser mais curtos (mínimo 10 chars)
      if (isHeading && rawText.length >= 10 && rawText.length < 200 && !seenTexts.has(rawText)) {
        seenTexts.add(rawText);
        paragraphs.push({
          type: 'heading',
          text: safeHtml, // Usar HTML seguro invés de texto puro
          level: parseInt(el.tagName.charAt(1))
        });
      }
      // Parágrafos normais
      else if (!isHeading) {
        if (seenTexts.has(rawText)) {
          duplicate++;
        } else if (rawText.length < 10) {
          tooShort++;
        } else if (!isValidText(rawText)) {
          junk++;
        } else {
          seenTexts.add(rawText);
          paragraphs.push({
            type: 'paragraph',
            text: safeHtml // Usar HTML seguro invés de texto puro
          });
        }
      }
    });

    console.log(`📊 CleanContentRenderer - Estatísticas:`, {
      total: textElements.length,
      extraídos: paragraphs.length,
      filtrados: filtered,
      muitoCurtos: tooShort,
      duplicados: duplicate,
      lixo: junk
    });

    return paragraphs;
  };

  /**
   * Filtra imagens válidas - ULTRA AGRESSIVO
   * Remove SVGs, ícones, logos, assets, widgets, etc.
   */
  const filterValidImages = (images) => {
    if (!Array.isArray(images) || images.length === 0) return [];

    console.log('🔍 Filtrando imagens no frontend:', images.length, 'imagens recebidas');

    const filtered = images.filter(img => {
      if (!img || typeof img !== 'string' || img.length < 20) return false;

      const imgLower = img.toLowerCase();

      // ❌ PADRÕES PARA EXCLUIR (ultra agressivo - frontend como camada extra)
      const invalidPatterns = [
        // Formatos
        '.svg', '.gif',

        // Ícones e UI
        '/icon', 'icon-', 'icon_', 'icons/',
        '/logo', 'logo-', 'logo_',
        'favicon', 'avatar', 'sprite',

        // Dimensões REALMENTE pequenas (thumbnails)
        '16x16', '32x32', '48x48', '64x64', '96x96', '128x128', '150x150',
        '-thumb.', 'thumbnail.', '_thumb.', // Com ponto (arquivo)
        '-150x150', '-128x128', '-96x96', '-64x64', '-32x32', // WordPress thumbnails

        // Assets e widgets (WM1 específico)
        '/assets/', '/wm1/assets/',
        'etagdigital', 'jazz.etagdigital',
        'tagwidform', 'cdn.jazz',

        // Placeholders
        'placeholder', 'loading', 'lazy',
        'data:image',

        // Social (botões/ícones de compartilhamento - NÃO bloquear CDNs de imagem)
        'social', 'share', 'button',
        'whatsapp'
        // REMOVIDO: 'facebook', 'twitter', 'instagram' - bloqueava CDNs de imagem válidos!
      ];

      if (invalidPatterns.some(pattern => imgLower.includes(pattern))) {
        console.log('  ❌ Filtrado:', img.substring(0, 80));
        return false;
      }

      // ✅ Aceitar URLs de proxy do nosso próprio backend
      // Ex: http://localhost:5000/api/proxy/instagram-image?url=https%3A%2F%2Fscontent...jpg
      // O .jpg está dentro do parâmetro ?url=, não na URL diretamente — por isso precisa de regra especial
      const isOurProxy = imgLower.includes('/api/proxy/instagram-image') ||
        imgLower.includes('/api/proxy/image');

      if (isOurProxy) {
        console.log('  ✅ Aceito (proxy local):', img.substring(0, 80));
        return true;
      }

      // ✅ Aceitar apenas imagens válidas
      const validExts = ['.jpg', '.jpeg', '.png', '.webp'];
      const hasValidExt = validExts.some(ext => imgLower.includes(ext));

      // ✅ Aceitar URLs do Twitter (formato especial)
      // Ex: https://pbs.twimg.com/media/ID?format=jpg&name=large
      const isTwitterImage = imgLower.includes('pbs.twimg.com/media/') &&
        (imgLower.includes('format=jpg') ||
          imgLower.includes('format=jpeg') ||
          imgLower.includes('format=png') ||
          imgLower.includes('format=webp'));

      // ✅ Aceitar URLs do Instagram CDN
      // Ex: https://scontent.cdninstagram.com/v/t39.30808-6/xxx.jpg
      const isInstagramImage = imgLower.includes('cdninstagram.com') ||
        imgLower.includes('instagram.com/p/');

      // ✅ Aceitar URLs especiais de sites que redirecionam (InfoMoney, etc)
      // Ex: https://www.infomoney.com.br/30296_2_l/ → redireciona para .jpg
      const isInfoMoneyRedirect = /infomoney\.com\.br\/\d+_\d+_[a-z]+\/?$/i.test(img);

      if (hasValidExt || isTwitterImage || isInstagramImage || isInfoMoneyRedirect) {
        console.log('  ✅ Aceito:', img.substring(0, 80));
        return true;
      }

      console.log('  ⚠️  Sem extensão válida:', img.substring(0, 80));
      return false;
    });

    // DEDUPLICAÇÃO: Remover URLs duplicadas (normalizar para comparação)
    const seen = new Set();
    const deduplicated = [];

    // Helper para extrair ID único da imagem
    const getImageId = (url) => {
      try {
        const urlObj = new URL(url);

        // Se for proxy nosso, extrair a URL original
        if (urlObj.pathname.includes('/api/proxy/instagram-image')) {
          const originalUrl = urlObj.searchParams.get('url');
          if (originalUrl) {
            return getImageId(originalUrl);
          }
        }

        // Para Instagram, tentar extrair o ID do path
        // Ex: /v/t51.2885-15/348356396_231966849488439_693963495475225091_n.jpg
        const parts = urlObj.pathname.split('/');
        const filename = parts[parts.length - 1];

        // Se tiver ID numérico longo (padrao Instagram/FB), usar ele
        const idMatch = filename.match(/(\d+_)+n\.(jpg|png|webp)/);
        if (idMatch) return idMatch[0];

        // Fallback: usar pathname completo normalizado
        return urlObj.pathname.toLowerCase();
      } catch {
        return url.toLowerCase();
      }
    };

    filtered.forEach(img => {
      const id = getImageId(img);

      if (!seen.has(id)) {
        seen.add(id);
        deduplicated.push(img);
      } else {
        console.log('  🔄 Duplicata removida (por ID):', img.substring(0, 80));
      }
    });

    console.log('✅ Total de imagens após filtro e deduplicação:', deduplicated.length);

    return deduplicated;
  };

  // Processar conteúdo baseado no formato
  let textBlocks = [];
  let validImages = [];

  if (isStructuredContent) {
    // NOVO: Usar formato JSON estruturado
    console.log('🎯 CleanContentRenderer: Usando formato JSON estruturado');
    const structuredData = convertStructuredContent(contentBlocks);
    textBlocks = structuredData.textBlocks;

    // Para imagens estruturadas, usar as do contentBlocks + allImages (fallback)
    const structuredImages = structuredData.imageBlocks.map(img => img.src);
    validImages = filterValidImages([...structuredImages, ...allImages]);
  } else {
    // LEGACY: Usar formato HTML
    console.log('📄 CleanContentRenderer: Usando formato HTML legado');
    textBlocks = extractCleanText(htmlContent || '');
    validImages = filterValidImages(allImages);
  }

  console.log('🎨 CleanContentRenderer:', {
    textBlocks: textBlocks.length,
    validImages: validImages.length,
    sampleText: textBlocks.slice(0, 2)
  });

  /**
   * Renderizar de forma ULTRA LIMPA:
   * - TikTok: Embed do vídeo + texto
   * - Twitter: Galeria de imagens no topo + texto
   * - Outras mídias: Texto com imagens intercaladas a cada 3-4 parágrafos
   */
  const renderCleanContent = () => {
    const elements = [];
    let imageIndex = 0;

    // Para TikTok, renderizar o embed do vídeo
    if (mediaType === 'TikTok' && embedHtml) {
      // Caminho principal: iframe direto (mais leve e estavel que blockquote + embed.js)
      if (tiktokVideoId) {
        elements.push(
          <Box
            key="tiktok-iframe-container"
            sx={{
              width: '100%',
              minHeight: { xs: '680px', md: '500px' },
              display: 'flex',
              justifyContent: 'center',
              overflow: 'visible',
              '& iframe': {
                display: 'block',
                width: '325px',
                height: '745px',
                border: 'none',
                borderRadius: '8px',
                transformOrigin: 'center top',
                transform: { xs: 'scale(0.8)', md: 'scale(1.0)' }
              }
            }}
          >
            <iframe
              src={`https://www.tiktok.com/embed/v2/${tiktokVideoId}`}
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              title="TikTok video"
            />
          </Box>
        );
      } else {
        // Fallback: manter HTML original se nao for possivel extrair o videoId
        elements.push(
          <Box
            key="tiktok-embed-fallback"
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              '& iframe': {
                maxWidth: '605px !important',
                borderRadius: '20px !important'
              }
            }}
            dangerouslySetInnerHTML={{ __html: embedHtml }}
          />
        );
      }

      // Para Facebook, renderizar o embed do video/reel
      // IMPORTANTE: Nao renderizar embed se o video esta no Cloudinary
    } else if (mediaType === 'Facebook' && embedHtml && videoStorageMethod !== 'cloudinary') {
      elements.push(
        <Box
          key="facebook-embed"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
            '& iframe': {
              width: '100%',
              maxWidth: '100%',
              borderRadius: '8px'
            }
          }}
          dangerouslySetInnerHTML={{ __html: embedHtml }}
        />
      );

      // Adicionar divisor após o embed se houver texto
      if (textBlocks.length > 0) {
        elements.push(
          <Box key="facebook-divider" sx={{ my: 3, borderBottom: '1px solid', borderColor: 'divider' }} />
        );
      }

      // Renderizar texto do Facebook (título/descrição)
      textBlocks.forEach((block, index) => {
        if (block.type === 'heading') {
          elements.push(
            <Typography
              key={`facebook-heading-${index}`}
              variant="h6"
              component="h2"
              sx={{
                mt: 3,
                mb: 1.5,
                fontWeight: 600,
                lineHeight: 1.4,
                color: 'primary.main'
              }}
              dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
            />
          );
        } else if (block.type === 'text') {
          elements.push(
            <Typography
              key={`facebook-text-${index}`}
              variant="body1"
              paragraph
              sx={{
                mb: 2,
                lineHeight: 1.8,
                color: 'text.primary',
                textAlign: 'justify'
              }}
              dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
            />
          );
        }
      });

      return elements;
    }

    // Para Instagram, renderizar o embed
    if (mediaType === 'Instagram' && embedHtml) {
      console.log('📸 Renderizando embed do Instagram');

      elements.push(
        <Box
          key="instagram-embed"
          ref={instagramContainerRef}
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
            '& .instagram-media': {
              margin: '0 auto !important',
              maxWidth: '540px !important',
              minWidth: '326px !important'
            },
            '& iframe': {
              maxWidth: '100%',
              borderRadius: '8px'
            }
          }}
          dangerouslySetInnerHTML={{ __html: embedHtml }}
        />
      );

      // Adicionar divisor após o embed se houver texto
      if (textBlocks.length > 0) {
        elements.push(
          <Box key="instagram-divider" sx={{ my: 3, borderBottom: '1px solid', borderColor: 'divider' }} />
        );
      }

      // Renderizar texto do Instagram (título/descrição)
      textBlocks.forEach((block, index) => {
        if (block.type === 'heading') {
          elements.push(
            <Typography
              key={`heading-${index}`}
              variant={block.level <= 2 ? 'h5' : 'h6'}
              sx={{
                mt: 3,
                mb: 2,
                fontWeight: 600,
                color: 'text.primary'
              }}
              dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
            />
          );
        } else {
          elements.push(
            <Typography
              key={`para-${index}`}
              variant="body1"
              paragraph
              sx={{
                mb: 2,
                lineHeight: 1.8,
                color: 'text.primary',
                textAlign: 'justify'
              }}
              dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
            />
          );
        }
      });

      return elements;
    }

    // Para Twitter, renderizar TODAS as imagens no início como galeria
    if (mediaType === 'Twitter' && validImages.length > 0) {
      console.log('🐦 Renderizando galeria do Twitter:', validImages.length, 'imagens');

      validImages.forEach((imgSrc, idx) => {
        elements.push(
          <Box
            key={`twitter-img-${idx}`}
            sx={{
              width: '100%',
              textAlign: 'center',
              mb: 2,
              display: 'flex',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            <img
              src={imgSrc}
              alt={`Foto ${idx + 1} do Twitter`}
              style={{
                maxWidth: imageDimensions.maxWidth,
                width: imageDimensions.width,
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                objectFit: 'contain',
                display: 'block',
                margin: imageDimensions.margin
              }}
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        );
      });

      // Adicionar divisor após as imagens
      if (textBlocks.length > 0) {
        elements.push(
          <Box key="twitter-divider" sx={{ my: 3, borderBottom: '1px solid', borderColor: 'divider' }} />
        );
      }

      // Agora renderizar o texto (sem intercalar mais imagens)
      textBlocks.forEach((block, index) => {
        if (block.type === 'heading') {
          elements.push(
            <Typography
              key={`heading-${index}`}
              variant={block.level <= 2 ? 'h5' : 'h6'}
              sx={{
                mt: 3,
                mb: 2,
                fontWeight: 600,
                color: 'text.primary'
              }}
              dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
            />
          );
        } else {
          elements.push(
            <Typography
              key={`para-${index}`}
              variant="body1"
              paragraph
              sx={{
                mb: 2,
                lineHeight: 1.8,
                color: 'text.primary',
                textAlign: 'justify'
              }}
              dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
            />
          );
        }
      });

      return elements;
    }

    // Para Instagram, usar carrossel estilo Instagram com setas
    if (mediaType === 'Instagram' && validImages.length > 0) {
      console.log('📸 Renderizando carrossel do Instagram:', validImages.length, 'imagens');

      elements.push(
        <Box key="instagram-carousel" sx={{ mb: 2 }}>
          <ImageCarousel
            images={validImages}
            media={carouselMedia} // Passar media estruturada para suportar vídeos
            alt="Foto do Instagram"
            maxWidth={imageDimensions.maxWidth}
          />
        </Box>
      );

      return elements;
    }

    // Para outras mídias (não-Twitter/Instagram), usar o método original com imagens intercaladas
    textBlocks.forEach((block, index) => {
      // Renderizar texto
      if (block.type === 'heading') {
        elements.push(
          <Typography
            key={`heading-${index}`}
            variant={block.level === 1 ? 'h4' : block.level === 2 ? 'h5' : 'h6'}
            sx={{
              mt: block.level === 1 ? 0 : 3,
              mb: block.level === 1 ? 1 : 2,
              fontWeight: block.level <= 2 ? 700 : 600,
              color: 'text.primary',
              lineHeight: 1.3
            }}
            dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
          />
        );
      } else if (block.isAuthor) {
        // Linha do autor em itálico
        elements.push(
          <Typography
            key={`author-${index}`}
            variant="body2"
            sx={{
              mb: 3,
              fontStyle: 'italic',
              color: 'text.secondary'
            }}
            dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
          />
        );
      } else {
        elements.push(
          <Typography
            key={`para-${index}`}
            variant="body1"
            paragraph
            sx={{
              mb: 2,
              lineHeight: 1.8,
              color: 'text.primary',
              textAlign: 'justify'
            }}
            dangerouslySetInnerHTML={{ __html: block.text }} // Usar HTML
          />
        );
      }

      // Adicionar imagem a cada 3-4 parágrafos
      const shouldAddImage = (
        imageIndex < validImages.length &&
        index > 0 &&
        index % 3 === 0
      );

      if (shouldAddImage) {
        elements.push(
          <Box
            key={`img-${imageIndex}`}
            sx={{
              width: '100%',
              textAlign: 'center',
              my: 3,
              display: 'flex',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            <img
              src={validImages[imageIndex]}
              alt={`Imagem ${imageIndex + 1}`}
              style={{
                maxWidth: imageDimensions.maxWidth,
                width: imageDimensions.width,
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                objectFit: 'contain',
                display: 'block',
                margin: imageDimensions.margin
              }}
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        );
        imageIndex++;
      }
    });

    // Adicionar imagens restantes no final
    while (imageIndex < validImages.length) {
      elements.push(
        <Box
          key={`img-remaining-${imageIndex}`}
          sx={{
            width: '100%',
            textAlign: 'center',
            my: 3,
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <img
            src={validImages[imageIndex]}
            alt={`Imagem ${imageIndex + 1}`}
            style={{
              maxWidth: imageDimensions.maxWidth,
              width: imageDimensions.width,
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              objectFit: 'contain',
              display: 'block',
              margin: imageDimensions.margin
            }}
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Box>
      );
      imageIndex++;
    }

    return elements;
  };

  // Se não tem conteúdo (mas permitir TikTok e Facebook com embed)
  if (textBlocks.length === 0 && validImages.length === 0 && !((mediaType === 'TikTok' || mediaType === 'Facebook') && embedHtml)) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Conteúdo não disponível
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={`clean-content-renderer ${className}`}
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        p: 2,
        ...sx
      }}
    >
      {renderCleanContent()}
    </Box>
  );
};

CleanContentRenderer.propTypes = {
  // Novo formato (preferido)
  contentBlocks: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['text', 'image']).isRequired,
    content: PropTypes.string, // Para type: 'text'
    src: PropTypes.string,      // Para type: 'image'
    caption: PropTypes.string   // Para type: 'image'
  })),
  // Formato legado (fallback)
  htmlContent: PropTypes.string,
  allImages: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  sx: PropTypes.object,
  mediaType: PropTypes.string,
  contentType: PropTypes.string,
  // Para embeds de redes sociais (TikTok)
  embedHtml: PropTypes.string
};

export default CleanContentRenderer;

